import React, { useEffect, useState, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRoute, faPlus, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import Swal from 'sweetalert2';
import InternalShell, { useInternalApi } from '../layouts/InternalShell';
import { CRM_NAV, STAGE_COLORS } from './nav';

const STAGES = ['invited', 'registered', 'profile_complete', 'credentials_uploaded',
    'first_patient', 'first_record', 'active', 'stalled', 'withdrawn'];

const OnboardingPipelines: React.FC = () => {
    const { api } = useInternalApi();
    const [journeys, setJourneys] = useState<any[]>([]);
    const [funnel, setFunnel] = useState<any[]>([]);
    const [cohorts, setCohorts] = useState<string[]>([]);
    const [stalled, setStalled] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [cohortFilter, setCohortFilter] = useState('');
    const [showAdd, setShowAdd] = useState(false);
    const [form, setForm] = useState({ principal_type: 'provider', principal_id: '', principal_label: '', cohort: 'soft-launch-1' });

    useEffect(() => { document.title = 'Onboarding Pipelines - Uzima Internal'; }, []);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (cohortFilter) params.set('cohort', cohortFilter);
            const r = await api.get(`crm-api/onboarding/?${params}`);
            if (r.data?.status === 'success') {
                setJourneys(r.data.data.journeys || []);
                setFunnel(r.data.data.funnel || []);
                setCohorts(r.data.data.cohorts || []);
                setStalled(r.data.data.stalled || []);
            }
        } finally { setLoading(false); }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cohortFilter]);

    useEffect(() => { load(); }, [load]);

    const addToPipeline = async () => {
        if (!form.principal_id.trim()) {
            Swal.fire({ icon: 'warning', title: 'ID required', text: 'Enter the account ID to track.' });
            return;
        }
        try {
            await api.post('crm-api/onboarding/', form);
            setShowAdd(false);
            setForm({ principal_type: 'provider', principal_id: '', principal_label: '', cohort: 'soft-launch-1' });
            load();
        } catch (err: any) {
            Swal.fire({ icon: 'error', title: 'Error', text: err?.response?.data?.message || 'Could not add to pipeline.' });
        }
    };

    const setStage = async (j: any, stage: string) => {
        try {
            await api.patch(`crm-api/onboarding/${j.id}/`, { stage });
            load();
        } catch {
            Swal.fire({ icon: 'error', title: 'Error', text: 'Could not update stage.' });
        }
    };

    return (
        <InternalShell activeDashboard="customer_relations" subNav={CRM_NAV}>
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h2 className="fw-bold mb-1"><FontAwesomeIcon icon={faRoute} className="me-2 text-primary" />Onboarding Pipelines</h2>
                    <p className="text-muted mb-0">Track each new account from invitation through to active use</p>
                </div>
                <button className="btn btn-primary" onClick={() => setShowAdd(true)}><FontAwesomeIcon icon={faPlus} className="me-2" />Track Account</button>
            </div>

            {stalled.length > 0 && (
                <div className="alert alert-warning">
                    <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                    <strong>{stalled.length} account(s) have sat in the same stage for over 14 days.</strong> Silence usually means they have given up, not that all is well.
                </div>
            )}

            <div className="d-flex gap-2 mb-3 flex-wrap align-items-center">
                <select className="form-select" style={{ maxWidth: 220 }} value={cohortFilter} onChange={(e) => setCohortFilter(e.target.value)}>
                    <option value="">All cohorts</option>
                    {cohorts.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
                {funnel.map(f => (
                    <span key={f.stage} className={`badge bg-${STAGE_COLORS[f.stage] || 'secondary'} p-2`}>{f.stage.replace(/_/g, ' ')}: {f.count}</span>
                ))}
            </div>

            <div className="card border-0 shadow-sm">
                <div className="card-body p-0">
                    {loading ? (
                        <div className="text-center py-5"><div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading...</span></div></div>
                    ) : journeys.length === 0 ? (
                        <div className="text-center py-5 text-muted">No accounts being tracked yet.</div>
                    ) : (
                        <table className="table table-hover align-middle mb-0">
                            <thead className="table-light"><tr><th>Account</th><th>Cohort</th><th>Stage</th><th>Days in stage</th><th>Owner</th><th>Move to</th></tr></thead>
                            <tbody>
                                {journeys.map(j => (
                                    <tr key={j.id} className={j.days_in_stage > 14 && !['active', 'withdrawn'].includes(j.stage) ? 'table-warning' : ''}>
                                        <td><strong>{j.principal_label || j.principal_id}</strong><div className="small text-muted">{j.principal_type}</div></td>
                                        <td className="small">{j.cohort || '—'}</td>
                                        <td><span className={`badge bg-${STAGE_COLORS[j.stage] || 'secondary'}`}>{j.stage.replace(/_/g, ' ')}</span></td>
                                        <td>{j.days_in_stage}</td>
                                        <td className="small">{j.owner || '—'}</td>
                                        <td style={{ minWidth: 180 }}>
                                            <select className="form-select form-select-sm" value={j.stage} onChange={(e) => setStage(j, e.target.value)}>
                                                {STAGES.map(s => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
                                            </select>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>

            {showAdd && (
                <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.5)' }} tabIndex={-1}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Track an Account</h5>
                                <button type="button" className="btn-close" onClick={() => setShowAdd(false)}></button>
                            </div>
                            <div className="modal-body">
                                <div className="mb-3">
                                    <label className="form-label">Account type</label>
                                    <select className="form-select" value={form.principal_type} onChange={(e) => setForm(f => ({ ...f, principal_type: e.target.value }))}>
                                        <option value="provider">Practitioner</option>
                                        <option value="institution">Institution</option>
                                        <option value="staff">Clinic staff</option>
                                        <option value="patient">Patient</option>
                                    </select>
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Account ID</label>
                                    <input className="form-control" value={form.principal_id} onChange={(e) => setForm(f => ({ ...f, principal_id: e.target.value }))} />
                                    <div className="form-text">Find this on the Contact 360 page.</div>
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Display name</label>
                                    <input className="form-control" value={form.principal_label} onChange={(e) => setForm(f => ({ ...f, principal_label: e.target.value }))} />
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Cohort</label>
                                    <input className="form-control" value={form.cohort} onChange={(e) => setForm(f => ({ ...f, cohort: e.target.value }))} />
                                    <div className="form-text">Groups an intake, e.g. soft-launch-1.</div>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button className="btn btn-light" onClick={() => setShowAdd(false)}>Cancel</button>
                                <button className="btn btn-primary" onClick={addToPipeline}>Track</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </InternalShell>
    );
};

export default OnboardingPipelines;
